import { EditOutlined, LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useClassifierServiceDeleteReferenceIntervalClassifierElementEndpoint,
  useClassifierServiceGetReferenceIntervalClassifierElementListEndpoint,
  useClassifierServiceGetReferenceIntervalClassifierElementListEndpointKey
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import {
  DeleteConfirm,
  message,
  PageBreadcrumbs,
  TableActionButtons
} from 'src/components'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { ReferenceIntervalModal } from '../components'

export const ReferenceIntervalsPage = () => {
  const { t, currentLang } = useLocales()
  const navigate = useNavigate()

  const pagination = useTablePagination()
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [currentId, setCurrentId] = useState<string>()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize
  }

  const { data, isLoading } =
    useClassifierServiceGetReferenceIntervalClassifierElementListEndpoint(
      queryParams
    )

  const { mutate } =
    useClassifierServiceDeleteReferenceIntervalClassifierElementEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        message.success(t('laboratory_classifiers.delete_success_option'))
        queryClient.invalidateQueries({
          queryKey: [
            useClassifierServiceGetReferenceIntervalClassifierElementListEndpointKey
          ]
        })
      }
    })

  const onToggle = () => setIsOpen((prev) => !prev)

  const onCloseModal = () => {
    onToggle()
    setCurrentId(undefined)
  }

  const onBackClick = () => navigate(-1)

  const getItems = (id: string): MenuProps['items'] => [
    {
      key: 'edit',
      label: t('common.edit'),
      icon: <EditOutlined />,
      onClick: () => {
        setCurrentId(id)
        onToggle()
      }
    },
    {
      key: 'delete',
      danger: true,
      label: (
        <DeleteConfirm
          title={t('laboratory_classifiers.delete_option')}
          onConfirm={() =>
            mutate({
              id: String(id)
            })
          }
        />
      )
    }
  ]

  const columns: ColumnsType = [
    {
      key: 'name',
      title: t('medicine_register.products.name'),
      render: ({ translation }) => translation?.[currentLang?.value]
    },
    {
      key: 'min value',
      title: t('laboratory_classifiers.min_value'),
      render: ({ minValue }) => minValue || '-'
    },
    {
      key: 'max value',
      title: t('laboratory_classifiers.max_value'),
      render: ({ maxValue }) => maxValue || '-'
    },
    {
      key: 'comments',
      title: t('common.comments'),
      render: (_, { comments }) => comments || '-'
    },
    {
      key: 'order',
      title: t('common.order'),
      render: (_, { order }) => order ?? '-'
    },
    {
      key: 'actions',
      render: ({ id }) => (
        <TableActionButtons justify="flex-end" items={getItems(id)} />
      )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('laboratory_classifiers.reference_intervals')}
      items={[
        { title: t('menu.laboratory_classifiers') },
        { title: t('laboratory_classifiers.reference_intervals') }
      ]}
    >
      <Flex vertical>
        <Flex justify="space-between">
          <Button icon={<LeftOutlined />} onClick={onBackClick}>
            {t('common.back')}
          </Button>

          <Button type="primary" onClick={onToggle}>
            {t('laboratory_classifiers.add_option')}
          </Button>
        </Flex>

        <Divider />

        <Table
          rowKey="id"
          columns={columns}
          loading={isLoading}
          dataSource={data?.data}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>

      {isOpen && (
        <ReferenceIntervalModal id={currentId} onCancel={onCloseModal} />
      )}
    </PageBreadcrumbs>
  )
}
